import { porter } from './porter.ts';
import { lesTall, type LeddNoekkel, type Skjema } from './skjema.ts';

/**
 * Setningen (P1, produkteier 2026-09-26), bygget med de SAMME ordene som tjenerens `sentence`
 * (H1.4, `HypothesisForms.Sentence`):
 *
 *   «Vi tror at {change} for {audience} gir {effect}, målt ved {metricName} fra {sourceText}.
 *    Vi har rett hvis [{baselineValue} {unit} →] {minst | høyst} {threshold} {unit} innen {testEndsOn}.»
 *
 * Pilen står bare når baseline er målt. Datoen i panelets kortform («30. nov»), tallet med
 * desimalkomma uten etterfølgende nuller. Endres ordene her, endres de hos tjeneren samme dag
 * — testen `h22-setning.test.ts` holder de fire tjenersetningene ordrett.
 */

const MAANEDER = ['jan', 'feb', 'mar', 'apr', 'mai', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'des'];

/** Tallet med desimalkomma og uten etterfølgende nuller: 46 · 2,5 · 4,25. */
export const tall = (n: number): string => String(n).replace('.', ',');

/** «ÅÅÅÅ-MM-DD» → «30. nov», uten tidssone: en `plainDate` er en dato, ikke et tidspunkt. */
export function kortDato(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return iso;
  return `${Number(m[3])}. ${MAANEDER[Number(m[2]) - 1] ?? ''}`;
}

/** Et tidspunkt (`utcDateTime`) som dato i Europe/Oslo: «28. okt». */
export function kortDatoAv(tidspunkt: string): string {
  const d = new Date(tidspunkt);
  const deler = new Intl.DateTimeFormat('nb-NO', { timeZone: 'Europe/Oslo', year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(d);
  const hent = (t: string) => deler.find((p) => p.type === t)?.value ?? '';
  return kortDato(`${hent('year')}-${hent('month')}-${hent('day')}`);
}

/** Et tidspunkt som dato i Europe/Oslo med årstallet: «2. nov 2026» (H2.8, DD-80 — fortid, ofte et annet år). */
export function kortDatoMedAarAv(tidspunkt: string): string {
  const aar = new Intl.DateTimeFormat('nb-NO', { timeZone: 'Europe/Oslo', year: 'numeric' }).format(new Date(tidspunkt));
  return `${kortDatoAv(tidspunkt)} ${aar}`;
}

/** Dagens dato i Europe/Oslo som «ÅÅÅÅ-MM-DD» — grensen for en dato som ikke kan være i framtiden. */
export function iDagIso(): string {
  const deler = new Intl.DateTimeFormat('nb-NO', { timeZone: 'Europe/Oslo', year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(new Date());
  const hent = (t: string) => deler.find((p) => p.type === t)?.value ?? '';
  return `${hent('year')}-${hent('month')}-${hent('day')}`;
}

const MAANEDER_LANG =['januar', 'februar', 'mars', 'april', 'mai', 'juni', 'juli', 'august', 'september', 'oktober', 'november', 'desember'];

/** Et tidspunkt som dato med hele månedsnavnet i Europe/Oslo: «7. desember» (M10c). */
export function langDatoAv(tidspunkt: string): string {
  const deler = new Intl.DateTimeFormat('nb-NO', { timeZone: 'Europe/Oslo', month: 'numeric', day: 'numeric' }).formatToParts(new Date(tidspunkt));
  const hent = (t: string) => Number(deler.find((p) => p.type === t)?.value ?? '0');
  return `${hent('day')}. ${MAANEDER_LANG[hent('month') - 1] ?? ''}`;
}

/**
 * `HypothesisCounts.since` («ÅÅÅÅ-MM-DD») som tellelinjens «siden mars» (W9): måneden med liten
 * forbokstav, og årstallet bare når det ikke er inneværende år («siden mars 2025»).
 */
export function sidenTekst(since: string, naa: Date = new Date()): string | null {
  const m = /^(\d{4})-(\d{2})-\d{2}$/.exec(since);
  const maaned = m ? MAANEDER_LANG[Number(m[2]) - 1] : undefined;
  if (!m || !maaned) return null;
  return Number(m[1]) === naa.getFullYear() ? `siden ${maaned}` : `siden ${maaned} ${m[1]}`;
}

const RETNING = { minst: 'minst', hoyst: 'høyst' } as const;

/** «minst» eller «høyst», som i setningen. */
export const retningOrd = (d: keyof typeof RETNING): string => RETNING[d];

/** Tjenerens setning, eller `null` til formen er komplett — som kontrakten sier om `sentence`. */
export function setningFra(s: Skjema, min: number): string | null {
  if (porter(s, min).some((m) => m.felt !== 'whatWeLose')) return null;
  return tekstAv(delsetning(s));
}

/**
 * En brikke i setningen. `fw` er bindeord (retningens, over); `f` er fylt; `e` er tom med
 * plassholderen; `on` er leddet som skrives nå; `mangler` er det porten mangler, med ord
 * (DD-08: ⚠ + ord, aldri farge alene). `ledd` er leddet brikken åpner.
 */
export interface Del {
  type: 'fw' | 'f' | 'e' | 'on' | 'mangler';
  tekst: string;
  ledd?: LeddNoekkel;
  /** Endringsskjemaet under 900 (M4d): leddet avviker fra gjeldende form — blyant + «Endret» i navnet. */
  endret?: boolean;
}

/**
 * Delsetningen fra det som er skrevet. `aapent` er leddet som skrives nå; `visMangler` gjør
 * tomme brikker til ⚠-brikker i «Les over», og legger «enhet» og «datoen» til terskelen når
 * bare de mangler — samme ord som i låsen i foten.
 */
export function delsetning(s: Skjema, opts: { aapent?: LeddNoekkel; visMangler?: boolean; kildenavn?: string } = {}): Del[] {
  const { aapent, visMangler = false } = opts;
  // H3.3: en kilde fra katalogen står med navnet, som tjenerens setning (`sourceName`).
  const kilde = s.metricSourceId ? (opts.kildenavn ?? 'kilden fra kildelisten') : s.sourceText;
  const brikke = (ledd: LeddNoekkel, verdi: string, plass: string): Del => {
    const v = verdi.trim();
    if (ledd === aapent) return { type: 'on', tekst: v || plass, ledd };
    if (v) return { type: 'f', tekst: v, ledd };
    return { type: visMangler ? 'mangler' : 'e', tekst: plass, ledd };
  };

  const enhet = s.unit.trim();
  const medEnhet = (n: number) => (enhet ? `${tall(n)} ${enhet}` : tall(n));
  const terskelTall = lesTall(s.threshold);
  const baselineTall = lesTall(s.baselineValue);
  const pil = s.baselineState === 'maalt' && baselineTall !== undefined ? `${medEnhet(baselineTall)} → ` : '';

  const terskel: Del[] = [];
  if (terskelTall !== undefined && s.direction) {
    terskel.push({ type: aapent === 'rett' ? 'on' : 'f', tekst: `${pil}${RETNING[s.direction]} ${medEnhet(terskelTall)}`, ledd: 'rett' });
    if (!enhet && visMangler) terskel.push({ type: 'mangler', tekst: 'enhet', ledd: 'rett' });
  } else {
    terskel.push({ type: aapent === 'rett' ? 'on' : visMangler ? 'mangler' : 'e', tekst: 'terskelen', ledd: 'rett' });
  }
  const dato: Del = s.testEndsOn ? { type: aapent === 'rett' ? 'on' : 'f', tekst: kortDato(s.testEndsOn), ledd: 'rett' } : { type: aapent === 'rett' ? 'on' : visMangler ? 'mangler' : 'e', tekst: 'datoen', ledd: 'rett' };

  return [
    { type: 'fw', tekst: 'Vi tror at' },
    brikke('endring', s.change, 'endringen'),
    { type: 'fw', tekst: 'for' },
    brikke('endring', s.audience, 'hvem'),
    { type: 'fw', tekst: 'gir' },
    brikke('effekt', s.effect, 'effekten'),
    { type: 'fw', tekst: ', målt ved' },
    brikke('maaltall', s.metricName, 'måltallet'),
    { type: 'fw', tekst: 'fra' },
    brikke('kilde', kilde, 'kilden'),
    { type: 'fw', tekst: '. Vi har rett hvis' },
    ...terskel,
    { type: 'fw', tekst: 'innen' },
    dato,
    { type: 'fw', tekst: '.' },
  ];
}

/** Mellomrom før hver del, unntatt før komma og punktum — samme regel som brikkene tegnes med. */
export const mellomromFoer = (d: Del, i: number): boolean => i > 0 && !/^[,.]/.test(d.tekst);

export const tekstAv = (deler: Del[]): string => deler.map((d, i) => (mellomromFoer(d, i) ? ` ${d.tekst}` : d.tekst)).join('');
